/** Self-serve billing helpers (pure): the landing page's "Get Pro" handoff, and how a subscription reads. The
 * wording helpers take `LuxWords` for the reader's language (the `lux` catalog); without it, English. */
import type { LuxWords } from "./lux.ts";

export type SelfServePlan = "pro" | "business";
export type Billing = "monthly" | "annual";
export type CheckoutIntent = { plan: SelfServePlan; billing: Billing };

export const INTENT_KEY = "solaros.checkoutIntent";

/** `?plan=pro&billing=annual` (the landing page's links, through sign-up): a self-serve plan; billing defaults to
 * annual. Null for anything else (Free, Enterprise, unknown). */
export function parseCheckoutIntent(search: string): CheckoutIntent | null {
  const params = new URLSearchParams(search);
  const plan = (params.get("plan") || params.get("upgrade") || "").toLowerCase();
  if (plan !== "pro" && plan !== "business") return null;
  const billing = (params.get("billing") || "annual").toLowerCase();
  return { plan, billing: billing === "monthly" ? "monthly" : "annual" };
}

/** Where the dashboard opens the checkout prompt for an intent. */
export function intentPath(intent: CheckoutIntent): string {
  return `/lux?upgrade=${intent.plan}&billing=${intent.billing}`;
}

/** A subscription's state in words, for the Plan & usage card. */
export function subscriptionLine(
  sub: { status: string; cancel_at_period_end: boolean; current_period_end?: string | null } | null | undefined,
  formatDate: (iso: string) => string,
  w?: LuxWords,
): string {
  const end = sub?.current_period_end ? formatDate(sub.current_period_end) : null;
  if (w) {
    const { t } = w;
    if (!sub) return t("lux.sub.none");
    if (sub.status === "past_due") return t("lux.sub.pastDue");
    if (sub.status === "canceled") return t("lux.sub.canceled");
    if (sub.status === "incomplete") return t("lux.sub.incomplete");
    if (sub.cancel_at_period_end) return end ? t("lux.sub.endsOn", { date: end }) : t("lux.sub.endsAtPeriodEnd");
    return end ? t("lux.sub.renewsOn", { date: end }) : t("lux.sub.active");
  }
  if (!sub) return "No subscription";
  if (sub.status === "past_due") return "Payment failed: update the payment method";
  if (sub.status === "canceled") return "Canceled";
  if (sub.status === "incomplete") return "Waiting for the payment";
  if (sub.cancel_at_period_end) return end ? `Ends on ${end}` : "Ends at the end of the period";
  return end ? `Renews on ${end}` : "Active";
}

/** What a year of annual billing saves over twelve monthly payments (cents; 0 when nothing). */
export function annualSaving(monthlyCents: number, annualMonthlyCents: number): number {
  return Math.max(0, (monthlyCents - annualMonthlyCents) * 12);
}

/* ------------------------------------------------------------------ extra users
 * The seat model: added users are charged at once for the rest of the period; removed ones stay paid until the
 * renewal (no credit), which bills the lower number. Seats renew with the plan, monthly or yearly. */

export type SeatSubscription = {
  status: string;
  quantity: number;
  current_period_start?: string | null;
  current_period_end?: string | null;
  scheduled_quantity?: number | null;
  scheduled_at?: string | null;
};

const LIVE_SEATS = ["active", "trialing", "past_due"];

/** The extra users paid for now (they count until a reduction's date). */
export function paidSeats(seats: SeatSubscription | null | undefined): number {
  return seats && LIVE_SEATS.includes(seats.status) ? seats.quantity : 0;
}

/** The extra users the company asked for: a waiting reduction's number, else the paid ones. */
export function wantedSeats(seats: SeatSubscription | null | undefined): number {
  const paid = paidSeats(seats);
  return paid && seats?.scheduled_quantity != null ? seats.scheduled_quantity : paid;
}

const users = (n: number) => `${n} extra user${n === 1 ? "" : "s"}`;
const per = (billing: Billing) => (billing === "annual" ? "a year" : "a month");

/** The price of an extra user, said the way it is billed: "$29 a month each", or "$243.60 a year each (annual
 * plan), 30% off monthly" (the plan's annual seat discount; `onPlan` false on Free, which has no plan billing). */
export function seatPriceText(amountCents: number, billing: Billing, money: (cents: number) => string, discountPct = 0, onPlan = true, w?: LuxWords): string {
  if (w) {
    if (billing !== "annual") return w.t("lux.seatPrice.monthly", { price: money(amountCents) });
    const off = discountPct > 0 ? new Intl.NumberFormat(w.locale, { style: "percent" }).format(discountPct / 100) : "none";
    return w.t("lux.seatPrice.annual", { price: money(amountCents), onPlan: onPlan ? "yes" : "no", off });
  }
  if (billing !== "annual") return `${money(amountCents)} a month each`;
  return `${money(amountCents)} a year each${onPlan ? " (annual plan)" : ""}${discountPct > 0 ? `, ${discountPct}% off monthly` : ""}`;
}

/** What `added` users cost today: the rest of the period (start..end), prorated. 0 when the period is unknown. */
export function prorationCents(amountCents: number, added: number, start: string | null | undefined,
                               end: string | null | undefined, now: Date = new Date()): number {
  if (added <= 0 || !start || !end) return 0;
  const from = Date.parse(start), to = Date.parse(end);
  if (!(to > from)) return 0;
  const left = Math.min(1, Math.max(0, (to - now.getTime()) / (to - from)));
  return Math.round(amountCents * added * left);
}

/** A change from the paid users to `wanted`, in words, before it is saved (null when nothing changes). */
export function seatChangeText(opts: {
  paid: number;
  wanted: number;
  current: number;
  amountCents: number;
  billing: Billing;
  periodStart?: string | null;
  periodEnd?: string | null;
  now?: Date;
  money: (cents: number) => string;
  formatDate: (iso: string) => string;
  words?: LuxWords;
}): string | null {
  const { paid, wanted, current, amountCents, billing, money } = opts;
  if (wanted === current) return null;
  const end = opts.periodEnd ? opts.formatDate(opts.periodEnd) : null;
  if (opts.words) {
    const { t } = opts.words;
    const per = billing === "annual" ? "year" : "month";
    if (wanted > paid) {
      const today = prorationCents(amountCents, wanted - paid, opts.periodStart, opts.periodEnd, opts.now);
      return t("lux.seatChange.add", { today: money(today), end: end ?? "none", then: money(amountCents * wanted), per, count: wanted });
    }
    if (wanted === paid) return t("lux.seatChange.keep", { count: paid });
    return t("lux.seatChange.reduce", { paid, end: end ?? "none", count: wanted, price: money(amountCents * wanted), per });
  }
  const then = wanted ? `${money(amountCents * wanted)} ${per(billing)} for ${users(wanted)}` : "no extra users";
  if (wanted > paid) {
    const today = prorationCents(amountCents, wanted - paid, opts.periodStart, opts.periodEnd, opts.now);
    return `${money(today)} today for the rest of this period${end ? ` (until ${end})` : ""}, then ${then}.`;
  }
  if (wanted === paid) return `Keeps your ${users(paid)}: nothing to pay, nothing changes at the renewal.`;
  const later = wanted ? `${users(wanted)} (${money(amountCents * wanted)} ${per(billing)})` : "none";
  return `Your ${users(paid)} stay until ${end ?? "the end of this period"}; from then you pay for ${later}. No credit for the rest of this period.`;
}

/** A reduction waiting for the renewal, in words (null when none). */
export function pendingSeatsText(seats: SeatSubscription | null | undefined, formatDate: (iso: string) => string, w?: LuxWords): string | null {
  const paid = paidSeats(seats);
  if (!seats || !paid || seats.scheduled_quantity == null || seats.scheduled_quantity >= paid) return null;
  if (w) return w.t("lux.seatPending", { when: seats.scheduled_at ? formatDate(seats.scheduled_at) : "none", count: seats.scheduled_quantity, paid });
  const when = seats.scheduled_at ? formatDate(seats.scheduled_at) : "the renewal";
  const next = seats.scheduled_quantity;
  return `From ${when} you pay for ${next ? users(next) : "no extra users"}. Your ${users(paid)} stay until then.`;
}
